import { Back, TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import * as Topics from "../Data/Topics";

const SS = Constants.SPRITESHEET_UI;

export class DanceMeter extends PIXI.Container {
    // protected text: PIXI.extras.BitmapText;
    protected score: number = 0;
    private readonly fill: PIXI.Sprite;
    private readonly luna: PIXI.Sprite;
    private readonly fillMask: PIXI.Graphics;
    private readonly marker1: PIXI.Sprite;
    private readonly marker2: PIXI.Sprite;
    private readonly marker3: PIXI.Sprite;
    private timeSinceLastIncrement: number = 0;
    private _introTimeline?: TimelineLite;

    private readonly DECAY_PER_SECOND = 0;
    private readonly DELAY_BEFORE_DECAY = 5;
    private readonly MAX_SCORE = 100;

    private unlock1: boolean = false;
    private unlock2: boolean = false;
    private unlock3: boolean = false;

    constructor() {
        super();

        PubSub.subscribe(Topics.SOLO_REQUESTED,             () => this.incrementScore());
        PubSub.subscribe(Topics.SOLO_STARTED,               () => this.incrementScore(10, 10));
        PubSub.subscribe(Topics.CULTURE_CHANGE_BEGIN,       () => this.incrementScore());
        PubSub.subscribe(Topics.SAMPLE_PAD_BUTTON_CLICKED,  () => this.incrementScore(0.5));
        PubSub.subscribe(Topics.FX_BUTTON_CLICKED,          () => this.incrementScore());

        this.fill = HLF.PixiUtils.makeSprite("dance-meter-fill", SS);
        this.fillMask = new PIXI.Graphics();
        this.fill.addChild(this.fillMask);
        this.fillMask.position.set(0, -this.fill.height / 2);
        this.fill.mask = this.fillMask;
        this.fillMask.beginFill(HLF.Colour.RED.toHex());
        this.fillMask.drawRect(0, 0, this.fill.width, this.fill.height);
        this.fillMask.endFill();

        this.fill.anchor.set(0, 0.5);

        this.marker1 = HLF.PixiUtils.makeSprite("marker-inactive", SS);
        this.marker2 = HLF.PixiUtils.makeSprite("marker-inactive", SS);
        this.marker3 = HLF.PixiUtils.makeSprite("marker-inactive", SS);

        this.luna = HLF.PixiUtils.makeSprite("dance-meter-lunaicon0", SS);
        this.luna.anchor.set(0.5);

        const container = HLF.PixiUtils.makeSprite("dance-meter-container", SS);
        const notfilled = HLF.PixiUtils.makeSprite("dance-meter-notfilled", SS);
        notfilled.anchor.set(0, 0.5);
        notfilled.position.set(-container.width / 2 + 2, 0);

        notfilled.addChild(this.fill);

        notfilled.addChild(this.marker1);
        notfilled.addChild(this.marker2);
        notfilled.addChild(this.marker3);
        notfilled.addChild(this.luna);

        container.addChild(notfilled);

        // this.fill.position.set(0, 0);

        this.marker1.position.set(this.fill.position.x + (this.fill.width / 3), -0.5);
        this.marker2.position.set(this.fill.position.x + (this.fill.width / 3 * 2), -0.5);
        this.marker3.position.set(this.fill.position.x + (this.fill.width), -0.5);

        this.addChild(
            HLF.PixiUtils.makeSprite("dance-meter-bg", SS),
            container,
        );

        // this.text = HLF.PixiUtils.makeText("0%", Constants.FONT_GEL_MED_22, 12, HLF.Colour.YELLOW.toHex());
        // this.addChild(this.text);

        this.visible = false; // onboarding
        this.position.set(
            -(1024 / 2) + (this.width / 2) + 72,
            (768 / 2) - (this.height / 2) - 230,
        );
    }
    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._introTimeline?.kill();
        TweenLite.killTweensOf(this.fill);
        TweenLite.killTweensOf(this.fillMask);
        TweenLite.killTweensOf(this.luna);
        TweenLite.killTweensOf(this.marker1);
        TweenLite.killTweensOf(this.marker2);
        TweenLite.killTweensOf(this.marker3);
        super.destroy(pOptions);
    }

    public update(pDeltaTime: number) {
        this.decay(pDeltaTime);
    }

    public startIntro() {
        this.fillMask.width = 0;
        const {x, y} = this.position;
        this.visible = true;
        this._introTimeline = new TimelineLite();
        this._introTimeline.delay(3);
        this._introTimeline
            .fromTo(this, 1,
                {
                    pixi: {
                        scale: 0,
                        positionX: 0,
                        positionY: 0,
                    },
                },
                {
                    pixi: {
                        scale: 1.5,
                    },
                    // ease: Back.easeOut.config(1.6),
                },
            )
            .to(this, 1, {
                pixi: {
                    scale: 1,
                    positionX: x,
                    positionY: y,
                },
                ease: Back.easeInOut.config(1.6),
            })
            .staggerFrom([this.marker1, this.marker2, this.marker3], 0.5, {pixi: {scale: 0}}, 0.4)
        ;
    }

    protected incrementScore(pByHowMuch: number = 1, pOverHowManySeconds: number = 0) {
        if (this._destroyed) { return; } // catch any remaining timeouts after object was destroyed

        const nextScore = HLF.MathUtils.clamp(this.score + pByHowMuch, 0, this.MAX_SCORE);

        // console.log({pByHowMuch, pOverHowManySeconds});

        if (pOverHowManySeconds > 0) {
            const secondsPerTick = 0.5;
            const howMuchPerTick = pOverHowManySeconds / pByHowMuch * secondsPerTick;
            const howManyTicks = pOverHowManySeconds / secondsPerTick;

            for (let i = 0; i < howManyTicks; i++) {
                setTimeout(() => {
                    this.incrementScore(howMuchPerTick, 0);
                }, i * secondsPerTick * 1000);
            }
        }
        else {
            const previousScore = this.score;
            this.score = nextScore;
            this.timeSinceLastIncrement = 0;
            this.onScoreChanged(previousScore, this.score);
        }
    }

    protected onScoreChanged(previousScore: number, newScore: number) {
        if (previousScore === newScore) { return; }

        const oneThird = (this.MAX_SCORE / 3);
        const twoThirds = (this.MAX_SCORE * 2 / 3);

        // this.text.text = (newScore / this.MAX_SCORE * 100).toFixed(0).toString() + "%";

        const percent = newScore / this.MAX_SCORE;

        TweenLite.to(this.fillMask, 0.5, {
            pixi: {width: HLF.MathUtils.clamp(percent, 0, 1) * this.fill.texture.width}},
        );

        TweenLite.to(this.luna, 0.5, {
            pixi: {positionX: HLF.MathUtils.clamp(percent, 0, 1) * this.fill.texture.width}},
        );

        // going up
        if (newScore >= oneThird && previousScore < oneThird) {
            // unlock first dance
            TweenLite.to(this.marker1, 0.5, {pixi: {scaleX: 1, scaleY: 1}, ease: Back.easeOut});
            this.marker1.texture = HLF.PixiUtils.getTexture("marker-active", SS);
            this.luna.texture = HLF.PixiUtils.getTexture("dance-meter-lunaicon1", SS);
            this.unlock1 = true;
            PubSub.publish(Topics.POWERUP_ONE_UNLOCKED, 0);
        }
        // going down
        else if (newScore < oneThird && previousScore >= oneThird) {
            this.marker1.texture = HLF.PixiUtils.getTexture("marker-inactive", SS);
        }

        // going up
        if (newScore >= twoThirds && previousScore < twoThirds) {
            // unlock second dance
            this.marker2.texture = HLF.PixiUtils.getTexture("marker-active", SS);
            this.luna.texture = HLF.PixiUtils.getTexture("dance-meter-lunaicon2", SS);
            this.unlock2 = true;
            PubSub.publish(Topics.POWERUP_TWO_UNLOCKED, 1);
        }
        // going down
        else if (newScore < twoThirds && previousScore >= twoThirds) {
            this.marker2.texture = HLF.PixiUtils.getTexture("marker-inactive", SS);
        }

        // going up
        if (newScore >= this.MAX_SCORE && previousScore < this.MAX_SCORE) {
            // unlock third dance
            this.marker3.texture = HLF.PixiUtils.getTexture("marker-active", SS);
            this.luna.texture = HLF.PixiUtils.getTexture("dance-meter-lunaicon3", SS);
            this.unlock3 = true;
            PubSub.publish(Topics.POWERUP_THREE_UNLOCKED, 2);
            TweenLite.to(this, 1.5, {pixi: {positionY: 256}, ease: Back.easeIn.config(2)});
        }
        else if (newScore < this.MAX_SCORE && previousScore >= this.MAX_SCORE) {
            this.marker3.texture = HLF.PixiUtils.getTexture("marker-inactive", SS);
        }
    }

    private decay(pDeltaTime: number) {
        this.timeSinceLastIncrement += pDeltaTime;
        if (this.DECAY_PER_SECOND > 0) {
            if (this.timeSinceLastIncrement >= this.DELAY_BEFORE_DECAY) {
                const previousScore = this.score;
                const oneThird = (this.MAX_SCORE / 3);
                const twoThirds = (this.MAX_SCORE * 2 / 3);

                this.score -= pDeltaTime * this.DECAY_PER_SECOND;

                this.score = HLF.MathUtils.clamp(this.score, 0, this.MAX_SCORE);

                if (this.unlock2) {
                    this.score = Math.max(twoThirds, this.score);
                }
                else if (this.unlock1) {
                    this.score = Math.max(oneThird, this.score);
                }

                this.onScoreChanged(previousScore, this.score);
            }
        }
    }
}
